import { CfFlowResponse } from '../api/cloudflareFlowApi';
import { CF_SETUP_STEPS, failedRowKeyOf, stepDisplayState } from '../components/cfSetupSteps';
import { redactCfSecrets } from '../redactCfSecrets';

/*
 * On secrets: every string that reaches the message goes through
 * `redactCfSecrets`, shared with `CloudflareErrorCard`, which renders the same
 * strings — two copies of a redaction rule is how one of them falls behind.
 *
 * That is the second lock, not the first. The source-side fix (PRE-3276) keeps
 * the credential out of `CloudflareFailure.message` to begin with. A summary
 * that leaves for a LangGraph thread we do not control must not depend on one
 * upstream fix staying correct.
 */

/** Where the customer asked for help. */
export type CfNexusHandoff =
  | 'preflight_failure'
  | 'deployment_failure'
  | 'rollback_failed';

/**
 * The two handoffs where Prerender had already started changing the customer's
 * account. They get their own shape: "help me finish" would otherwise ask Nexus
 * to continue from steps that were undone.
 */
const ROLLBACK_HANDOFFS: readonly CfNexusHandoff[] = [
  'deployment_failure',
  'rollback_failed',
];

/** Keeps one pasted stack trace from becoming the whole first message. */
export const MAX_ERROR_CHARS = 300;

const truncate = (value: string): string =>
  value.length <= MAX_ERROR_CHARS
    ? value
    : `${value.slice(0, MAX_ERROR_CHARS).trimEnd()}…`;

type Redact = (value: string) => string;

/** An empty field leaves its sentence out. It never becomes a placeholder. */
const clean = (
  redact: Redact,
  value: string | null | undefined,
): string | null => {
  const trimmed = value?.trim();

  return trimmed ? redact(trimmed) : null;
};

const endingInFullStop = (text: string): string =>
  text.endsWith('.') ? text : `${text}.`;

/** Drops a sentence whose field was empty, rather than writing "undefined". */
const sentence = (text: string | false | null): string =>
  text ? `${text} ` : '';

/** The step the flow stopped on, by the labels the progress screen shows. */
export const failedStepLabel = (flow: CfFlowResponse): string | null => {
  const failedKey = failedRowKeyOf(flow.steps);
  const failed =
    CF_SETUP_STEPS.find((step) => step.key === failedKey) ??
    CF_SETUP_STEPS.find((step) => stepDisplayState(step.key, flow.steps) === 'failed');
  if (failed) return failed.label;

  const running = CF_SETUP_STEPS.find(
    (step) => stepDisplayState(step.key, flow.steps) === 'running',
  );

  return running?.label ?? null;
};

const openingFor = (domain: string | null): string =>
  domain
    ? `I'm going through Prerender's automated Cloudflare setup (beta) for ${domain}.`
    : "I'm going through Prerender's automated Cloudflare setup (beta).";

const asMessage = (parts: readonly string[]): string => parts.join(' ').replace(/\s+/g, ' ').trim();

export type CfStuckPromptInput = {
  domain?: string | null;
  step?: string | null;
  /** The exact string on the screen, so Nexus reads what the customer reads. */
  error?: string | null;
  knownSecrets?: readonly string[];
};

export const TOKEN_STEP_LABEL = 'Connecting your Cloudflare token';

/** The PRD's standard prompt, for a customer stuck before Prerender changed anything. */
export function stuckPrompt({ domain, step, error, knownSecrets = [] }: CfStuckPromptInput): string {
  const redact: Redact = (value) => redactCfSecrets(value, knownSecrets);
  const stuckAt = clean(redact, step);
  const shown = clean(redact, error);

  return asMessage([
    openingFor(clean(redact, domain)),
    sentence(stuckAt && `I got stuck at: ${stuckAt}.`),
    sentence(shown && endingInFullStop(`Error shown: ${truncate(shown)}`)),
    'Can you help me finish the integration?',
  ]);
}

const START_OVER = 'Can you help me integrate with Cloudflare from the beginning?';

/** What the customer's Cloudflare account looks like now, and what to ask for. */
const deploymentClosing = (
  redact: Redact,
  flow: CfFlowResponse,
  leftovers: readonly string[],
): string => {
  const listed = leftovers
    .map((leftover) => clean(redact, leftover))
    .filter((item): item is string => item !== null);

  if (listed.length > 0) {
    return `These items are still in my Cloudflare account and need removing: ${listed.join(', ')}. Can you help me remove those and integrate with Cloudflare from the beginning?`;
  }

  if (flow.rollbackOutcome === 'succeeded') {
    return `Prerender undid all the changes, so my account is back to how it was. ${START_OVER}`;
  }

  if (flow.rollbackOutcome === 'failed') {
    return `Prerender could not undo all the changes it had made to my Cloudflare account. ${START_OVER}`;
  }

  if (flow.rollbackOutcome === 'not_needed') {
    return `Prerender didn't change anything in my Cloudflare account. ${START_OVER}`;
  }

  return START_OVER;
};

export type CfNexusSummaryInput = {
  handoff: CfNexusHandoff;
  flow: CfFlowResponse;
  /** The error Nexus is told about: the card's message, or Cloudflare's own text for a deployment. */
  errorMessage?: string | null;
  /** A step the caller fixed, named in place of the one that failed. */
  step?: string | null;
  /** What is still in the customer's Cloudflare account, as the error card lists it. */
  leftovers?: readonly string[];
  /**
   * Secrets the caller is holding, removed by exact match. The customer's
   * Prerender token belongs here: it is short base62 and no shape rule can
   * find it.
   */
  knownSecrets?: readonly string[];
};

/**
 * The first message the panel fills in (PRE-3293 / F5).
 *
 * Nexus never sends it. The customer reads it, edits it or deletes it — a
 * message sent on the customer's behalf is an action they did not take.
 */
export function buildNexusSummary({
  handoff,
  flow,
  errorMessage,
  step: fixedStep,
  leftovers = [],
  knownSecrets = [],
}: CfNexusSummaryInput): string {
  if (!ROLLBACK_HANDOFFS.includes(handoff)) {
    return stuckPrompt({ domain: flow.domain, step: fixedStep ?? failedStepLabel(flow), error: errorMessage, knownSecrets });
  }

  const redact: Redact = (value) => redactCfSecrets(value, knownSecrets);
  const step = clean(redact, fixedStep ?? failedStepLabel(flow));
  const error = clean(redact, errorMessage);

  return asMessage([
    openingFor(clean(redact, flow.domain)),
    sentence(step && `The setup failed at: ${step}.`),
    sentence(error && endingInFullStop(`Error shown: ${truncate(error)}`)),
    deploymentClosing(redact, flow, leftovers),
  ]);
}
